import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FlaskConical, Plus, Play, Pause, Trophy, TrendingUp,
  Mail, Eye, MousePointer, Users, Calendar, Clock,
  ChevronRight, X, Check, AlertCircle, BarChart3,
  ArrowUp, ArrowDown, Percent, Target, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const THEME = {
  bg: '#0f1117',
  cardBg: '#1a1d24',
  accent: '#10b981',
  textPrimary: '#f1f5f9',
  textSecondary: '#94a3b8',
  border: '#2d3748'
};

const TEMPLATE_TYPES = [
  { value: 'welcome', label: 'Welcome Email' },
  { value: 'followup', label: 'Follow Up' },
  { value: 'offer', label: 'Special Offer' },
  { value: 'brochure', label: 'Brochure/Info' },
  { value: 'closing', label: 'Deal Closing' },
  { value: 'reengagement', label: 'Re-engagement' }
];

const BUSINESS_TYPES = [
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'training', label: 'Training/Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'finance', label: 'Finance/Loans' },
  { value: 'travel', label: 'Travel' },
  { value: 'general', label: 'General' }
];

const ABTestingEmails = () => {
  const { businessType } = useBusinessContent();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchTests = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [testsRes, summaryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ab-testing/tests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/ab-testing/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTests(testsRes.data.tests || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch A/B tests:', error);
      toast.error('Failed to load A/B tests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const handleStopTest = async (testId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${BACKEND_URL}/api/ab-testing/tests/${testId}/stop`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test stopped');
      fetchTests();
    } catch (error) {
      toast.error('Failed to stop test');
    }
  };

  const handleDeclareWinner = async (testId, variant) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${BACKEND_URL}/api/ab-testing/tests/${testId}/declare-winner`, 
        { winning_variant: variant },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Variant ${variant.toUpperCase()} declared as winner!`);
      fetchTests();
      setSelectedTest(null);
    } catch (error) {
      toast.error('Failed to declare winner');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${BACKEND_URL}/api/ab-testing/tests/${testId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test deleted');
      fetchTests();
    } catch (error) {
      toast.error('Failed to delete test');
    }
  };

  const filteredTests = tests.filter(test => {
    if (filter === 'all') return true;
    return test.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'stopped': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: THEME.bg }}>
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ backgroundColor: THEME.bg }}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <FlaskConical className="w-7 h-7 text-emerald-500" />
              A/B Testing
            </h1>
            <p className="text-gray-400 mt-1">Test email variants to improve open and conversion rates</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New A/B Test
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0" style={{ backgroundColor: THEME.cardBg }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/20">
                    <FlaskConical className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{summary.summary.total_tests}</p>
                    <p className="text-xs text-gray-400">Total Tests</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ backgroundColor: THEME.cardBg }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <Play className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{summary.summary.running}</p>
                    <p className="text-xs text-gray-400">Running</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ backgroundColor: THEME.cardBg }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Trophy className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{summary.summary.completed}</p>
                    <p className="text-xs text-gray-400">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ backgroundColor: THEME.cardBg }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gray-500/20">
                    <Pause className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{summary.summary.stopped}</p>
                    <p className="text-xs text-gray-400">Stopped</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2">
          {['all', 'running', 'completed', 'stopped'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Tests List */}
        <div className="grid gap-4">
          {filteredTests.length === 0 ? (
            <Card className="border-0" style={{ backgroundColor: THEME.cardBg }}>
              <CardContent className="p-8 text-center">
                <FlaskConical className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No A/B Tests Yet</h3>
                <p className="text-gray-400 mb-4">Create your first A/B test to start optimizing your emails</p>
                <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-600 hover:bg-emerald-500">
                  <Plus className="w-4 h-4 mr-2" />
                  Create A/B Test
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredTests.map(test => (
              <TestCard
                key={test.id}
                test={test}
                onView={() => setSelectedTest(test)}
                onStop={() => handleStopTest(test.id)}
                onDelete={() => handleDeleteTest(test.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateTestModal
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              fetchTests();
            }}
            defaultBusinessType={businessType}
          />
        )}
      </AnimatePresence>

      {/* Test Details Modal */}
      <AnimatePresence>
        {selectedTest && (
          <TestDetailsModal
            test={selectedTest}
            onClose={() => setSelectedTest(null)}
            onDeclareWinner={handleDeclareWinner}
            onStop={() => {
              handleStopTest(selectedTest.id);
              setSelectedTest(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Test Card Component
const TestCard = ({ test, onView, onStop, onDelete }) => {
  const metrics = test.metrics || {};
  const leader = metrics.leader;
  const lift = metrics.lift_percentage || 0;

  const getStatusBadge = (status) => {
    const styles = {
      running: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      stopped: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return styles[status] || styles.stopped;
  };

  return (
    <Card 
      className="border-0 hover:ring-1 hover:ring-emerald-500/30 transition-all cursor-pointer"
      style={{ backgroundColor: THEME.cardBg }}
      onClick={onView}
    >
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Left: Test Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-white">{test.name}</h3>
              <Badge className={getStatusBadge(test.status)}>
                {test.status === 'running' && <Play className="w-3 h-3 mr-1" />}
                {test.status === 'completed' && <Trophy className="w-3 h-3 mr-1" />}
                {test.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {test.template_type}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {metrics.total_sent || 0} sent
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(test.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Middle: Metrics Comparison */}
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Variant A</p>
              <p className={`text-lg font-bold ${leader === 'a' ? 'text-emerald-400' : 'text-white'}`}>
                {metrics.variant_a?.open_rate || 0}%
              </p>
              <p className="text-xs text-gray-500">open rate</p>
            </div>
            <div className="text-gray-600">vs</div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Variant B</p>
              <p className={`text-lg font-bold ${leader === 'b' ? 'text-emerald-400' : 'text-white'}`}>
                {metrics.variant_b?.open_rate || 0}%
              </p>
              <p className="text-xs text-gray-500">open rate</p>
            </div>
          </div>

          {/* Right: Winner Indicator */}
          <div className="flex items-center gap-3">
            {test.winner ? (
              <div className="flex items-center gap-2 bg-yellow-500/20 px-3 py-2 rounded-lg">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 font-medium">
                  Winner: {test.winner.toUpperCase()}
                </span>
              </div>
            ) : leader && (
              <div className="flex items-center gap-2 bg-emerald-500/10 px-3 py-2 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400">
                  {leader.toUpperCase()} +{lift}%
                </span>
              </div>
            )}
            <ChevronRight className="w-5 h-5 text-gray-500" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Create Test Modal
const CreateTestModal = ({ onClose, onCreated, defaultBusinessType }) => {
  const [formData, setFormData] = useState({
    name: '',
    business_type: defaultBusinessType || 'general',
    template_type: 'welcome',
    split_ratio: 50,
    min_sample_size: 100,
    duration_days: 14,
    variant_a: { name: 'Control', subject: '', body: '' },
    variant_b: { name: 'Challenger', subject: '', body: '' }
  });
  const [creating, setCreating] = useState(false);
  const [step, setStep] = useState(1);

  const handleCreate = async () => {
    if (!formData.name || !formData.variant_a.subject || !formData.variant_b.subject) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${BACKEND_URL}/api/ab-testing/tests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('A/B test created successfully!');
      onCreated();
    } catch (error) {
      toast.error('Failed to create A/B test');
    } finally {
      setCreating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl"
        style={{ backgroundColor: THEME.cardBg }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 p-4 border-b border-gray-700 flex items-center justify-between" style={{ backgroundColor: THEME.cardBg }}>
          <div>
            <h2 className="text-xl font-bold text-white">Create A/B Test</h2>
            <p className="text-sm text-gray-400">Step {step} of 2</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {step === 1 ? (
            <>
              {/* Step 1: Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Test Name *</label>
                  <Input
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Welcome Email March 2026"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Business Type</label>
                    <Select
                      value={formData.business_type}
                      onValueChange={value => setFormData({ ...formData, business_type: value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUSINESS_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Template Type</label>
                    <Select
                      value={formData.template_type}
                      onValueChange={value => setFormData({ ...formData, template_type: value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Split Ratio (A%)</label>
                    <Input
                      type="number"
                      value={formData.split_ratio}
                      onChange={e => setFormData({ ...formData, split_ratio: parseInt(e.target.value) })}
                      min={10}
                      max={90}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Min Sample Size</label>
                    <Input
                      type="number"
                      value={formData.min_sample_size}
                      onChange={e => setFormData({ ...formData, min_sample_size: parseInt(e.target.value) })}
                      min={50}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Duration (days)</label>
                    <Input
                      type="number"
                      value={formData.duration_days}
                      onChange={e => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                      min={7}
                      max={30}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setStep(2)} className="bg-emerald-600 hover:bg-emerald-500">
                  Next: Configure Variants
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Step 2: Variants */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Variant A */}
                <div className="space-y-4 p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <span className="text-blue-400 font-bold">A</span>
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Variant A (Control)</h3>
                      <p className="text-xs text-gray-500">Current version</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Subject Line *</label>
                    <Input
                      value={formData.variant_a.subject}
                      onChange={e => setFormData({
                        ...formData,
                        variant_a: { ...formData.variant_a, subject: e.target.value }
                      })}
                      placeholder="Thank you for your enquiry!"
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Email Body *</label>
                    <Textarea
                      value={formData.variant_a.body}
                      onChange={e => setFormData({
                        ...formData,
                        variant_a: { ...formData.variant_a, body: e.target.value }
                      })}
                      placeholder="Enter email content..."
                      rows={6}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                </div>

                {/* Variant B */}
                <div className="space-y-4 p-4 rounded-lg bg-gray-800/50 border border-emerald-500/30">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <span className="text-emerald-400 font-bold">B</span>
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Variant B (Challenger)</h3>
                      <p className="text-xs text-gray-500">New version to test</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Subject Line *</label>
                    <Input
                      value={formData.variant_b.subject}
                      onChange={e => setFormData({
                        ...formData,
                        variant_b: { ...formData.variant_b, subject: e.target.value }
                      })}
                      placeholder="Your dream home awaits! 🏠"
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Email Body *</label>
                    <Textarea
                      value={formData.variant_b.body}
                      onChange={e => setFormData({
                        ...formData,
                        variant_b: { ...formData.variant_b, body: e.target.value }
                      })}
                      placeholder="Enter email content..."
                      rows={6}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button onClick={() => setStep(1)} variant="outline" className="border-gray-700 text-gray-300">
                  Back
                </Button>
                <Button 
                  onClick={handleCreate} 
                  disabled={creating}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  {creating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                  ) : (
                    <><FlaskConical className="w-4 h-4 mr-2" />Start A/B Test</>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Test Details Modal
const TestDetailsModal = ({ test, onClose, onDeclareWinner, onStop }) => {
  const metrics = test.metrics || {};
  const significance = test.statistical_significance || {};

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl"
        style={{ backgroundColor: THEME.cardBg }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 p-4 border-b border-gray-700 flex items-center justify-between" style={{ backgroundColor: THEME.cardBg }}>
          <div>
            <h2 className="text-xl font-bold text-white">{test.name}</h2>
            <p className="text-sm text-gray-400">
              {test.template_type} • {test.business_type} • Started {new Date(test.created_at).toLocaleDateString()}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Winner Banner (if significant) */}
          {test.status === 'running' && metrics.leader && metrics.lift_percentage > 15 && (
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-yellow-400" />
                  <div>
                    <p className="text-white font-medium">We have a clear winner!</p>
                    <p className="text-sm text-gray-400">
                      Variant {metrics.leader.toUpperCase()} is performing {metrics.lift_percentage}% better
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => onDeclareWinner(test.id, metrics.leader)}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Use Variant {metrics.leader.toUpperCase()}
                </Button>
              </div>
            </div>
          )}

          {/* Metrics Comparison */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Variant A */}
            <VariantMetricsCard
              variant="A"
              name={test.variant_a?.name || "Control"}
              subject={test.variant_a?.subject}
              data={test.variant_a}
              rates={metrics.variant_a}
              isWinning={metrics.leader === 'a'}
              isWinner={test.winner === 'a'}
            />

            {/* Variant B */}
            <VariantMetricsCard
              variant="B"
              name={test.variant_b?.name || "Challenger"}
              subject={test.variant_b?.subject}
              data={test.variant_b}
              rates={metrics.variant_b}
              isWinning={metrics.leader === 'b'}
              isWinner={test.winner === 'b'}
            />
          </div>

          {/* Statistical Significance */}
          <Card className="border-0" style={{ backgroundColor: '#1f2937' }}>
            <CardContent className="p-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
                Statistical Analysis
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Confidence Level</p>
                  <p className="text-2xl font-bold text-white">{significance.confidence || 0}%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Sample Progress</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${Math.min(significance.sample_progress || 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm text-white">{Math.round(significance.sample_progress || 0)}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Status</p>
                  <Badge className={significance.status === 'significant' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}>
                    {significance.status === 'significant' ? 'Statistically Significant' : 'Collecting Data'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {test.status === 'running' && (
            <div className="flex justify-between items-center pt-4 border-t border-gray-700">
              <Button
                onClick={onStop}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Pause className="w-4 h-4 mr-2" />
                Stop Test
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={() => onDeclareWinner(test.id, 'a')}
                  variant="outline"
                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                >
                  Declare A Winner
                </Button>
                <Button
                  onClick={() => onDeclareWinner(test.id, 'b')}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  Declare B Winner
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Variant Metrics Card
const VariantMetricsCard = ({ variant, name, subject, data, rates, isWinning, isWinner }) => {
  const bgColor = variant === 'A' ? 'bg-blue-500/10' : 'bg-emerald-500/10';
  const borderColor = isWinner ? 'border-yellow-500' : isWinning ? 'border-emerald-500/50' : 'border-gray-700';
  const accentColor = variant === 'A' ? 'text-blue-400' : 'text-emerald-400';

  return (
    <div className={`p-4 rounded-lg ${bgColor} border ${borderColor}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${variant === 'A' ? 'bg-blue-500/20' : 'bg-emerald-500/20'}`}>
            <span className={`font-bold ${accentColor}`}>{variant}</span>
          </div>
          <div>
            <h3 className="text-white font-medium">{name}</h3>
            <p className="text-xs text-gray-500 truncate max-w-[200px]">{subject}</p>
          </div>
        </div>
        {isWinner && (
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            <Trophy className="w-3 h-3 mr-1" />
            Winner
          </Badge>
        )}
        {isWinning && !isWinner && (
          <Badge className="bg-emerald-500/20 text-emerald-400">
            <TrendingUp className="w-3 h-3 mr-1" />
            Leading
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetricItem label="Sent" value={data?.sent || 0} icon={<Mail className="w-4 h-4" />} />
        <MetricItem label="Open Rate" value={`${rates?.open_rate || 0}%`} icon={<Eye className="w-4 h-4" />} />
        <MetricItem label="Click Rate" value={`${rates?.click_rate || 0}%`} icon={<MousePointer className="w-4 h-4" />} />
        <MetricItem label="Conversion" value={`${rates?.conversion_rate || 0}%`} icon={<Target className="w-4 h-4" />} />
      </div>
    </div>
  );
};

const MetricItem = ({ label, value, icon }) => (
  <div className="flex items-center gap-2">
    <div className="p-1.5 rounded bg-gray-800/50 text-gray-400">
      {icon}
    </div>
    <div>
      <p className="text-lg font-semibold text-white">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  </div>
);

export default ABTestingEmails;
